"use client";

import React, { useCallback, useEffect, useId, useRef, useState } from "react";

import { strings } from "@perceptron/constants";
import { cn } from "@perceptron/utils/cn";
import { MediaPlayerInstance } from "@vidstack/react";

import MediaControls from "./MediaControls";
import { TimelinePlayerBridge } from "./bridge";

const AUTO_HIDE_MS = 3000;

const MediaControlsMobileOverlay = ({
  playerRef,
  bridge,
  className,
}: {
  playerRef: React.RefObject<MediaPlayerInstance | null>;
  bridge: TimelinePlayerBridge;
  className?: string;
}) => {
  const [visible, setVisible] = useState(false);
  const panelId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const menuOpenRef = useRef(false);

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current !== null) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const scheduleHide = useCallback(() => {
    clearHideTimer();
    hideTimerRef.current = setTimeout(() => {
      hideTimerRef.current = null;
      if (!menuOpenRef.current && !panelRef.current?.contains(document.activeElement)) setVisible(false);
    }, AUTO_HIDE_MS);
  }, [clearHideTimer]);

  // Auto-dismiss whenever the controls are visible. Any interaction inside the
  // controls panel resets the timer via onPointerDown below.
  useEffect(() => {
    if (!visible) return;
    scheduleHide();
    return clearHideTimer;
  }, [visible, scheduleHide, clearHideTimer]);

  return (
    <div className={cn("absolute inset-0 z-20 flex items-center justify-center", className)}>
      <div
        className={cn(
          "pointer-events-none absolute inset-0 bg-black/60 transition-opacity duration-200",
          visible ? "opacity-100" : "opacity-0",
        )}
      />
      <button
        aria-controls={panelId}
        aria-expanded={visible}
        aria-label={visible ? strings.videoPlayer.hideControls : strings.videoPlayer.showControls}
        className="absolute inset-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring"
        type="button"
        onClick={() => setVisible((value) => !value)}
      />
      <div
        aria-hidden={!visible}
        className={cn(
          "relative flex flex-col items-center transition-opacity duration-200",
          visible ? "opacity-100" : "pointer-events-none opacity-0",
        )}
        id={panelId}
        inert={!visible}
        ref={panelRef}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) scheduleHide();
        }}
        onClick={(e) => e.stopPropagation()}
        onFocusCapture={clearHideTimer}
        onPointerDown={() => {
          if (visible) scheduleHide();
        }}
      >
        <MediaControls
          bridge={bridge}
          playerRef={playerRef}
          onMenuOpenChange={(open) => {
            menuOpenRef.current = open;
            if (open) clearHideTimer();
            else scheduleHide();
          }}
        />
      </div>
    </div>
  );
};

export default MediaControlsMobileOverlay;
